import React, { memo, useEffect, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { AddTab as AddTabOld } from '@atlassian/jira-business-add-tab/src/ui/index.tsx';
import { QueryError } from '@atlassian/jira-business-error-handling/src/utils/query-error/index.tsx';
import { ExperienceSuccess } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { ITEM_TYPE_KEY_TO_ROUTES } from '@atlassian/jira-business-navigation-common/src/index.tsx';
import { useShortcutsData } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/shortcuts/shortcuts-data/index.tsx';
import { ShortcutsContainer } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/shortcuts/state-container/index.tsx';
import { PROJECT } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/container-type.tsx';
import { TabsConfigurationProvider } from '@atlassian/jira-navigation-kit-common/src/common/tabs-configuration-context/index.tsx';
import type { RelayAGGError } from '@atlassian/jira-horizontal-nav-error-boundary/src/ui/agg-error-boundary/utils.tsx';
import TabList from '@atlassian/jira-navigation-kit-tab-list-old/src/ui/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line camelcase
import { DEPRECATED_DO_NOT_USE_projectDetailsResource } from '@atlassian/jira-router-resources-business-project-details/src/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import type { tabs_businessHorizontalNav_TabsContent$key } from '@atlassian/jira-relay/src/__generated__/tabs_businessHorizontalNav_TabsContent.graphql';
import { TAB, VIEW_HORIZONTAL_NAVIGATION_EXPERIENCE } from '../../../common/constants.tsx';
import { SkeletonComponent } from '../../../common/ui/skeleton/horizontal-nav/index.tsx';
import { tabsConfiguration } from '../../../common/utils.tsx';
import { useHideAppsTab } from '../../../controllers/hide-apps-tab/index.tsx';

type Props = {
	jiraQueryFragment: tabs_businessHorizontalNav_TabsContent$key;
	handleError: (error: Error | QueryError | RelayAGGError) => void;
};

/**
 * @deprecated remove when cleaning up getWillShowNav4
 */
export const TabsContent = ({ jiraQueryFragment, handleError }: Props) => {
	const { data: projectDetails } = useResource(DEPRECATED_DO_NOT_USE_projectDetailsResource);
	const { data: shortcutsData, loading } = useShortcutsData();
	/**
	 * edges field is included to allow the @connection directive to be used in the query,
	 * even though it is not used in the component.
	 * For more context, check this Github issue discussing the problem: https://github.com/facebook/relay/issues/1983
	 */
	const data = useFragment(
		graphql`
			fragment tabs_businessHorizontalNav_TabsContent on JiraQuery {
				navigationItems: jwmSavedViewsByProject(
					cloudId: $cloudId
					projectIdOrKey: $projectIdOrKey
					first: $first
				) @connection(key: "tabs_jiraNavigation__navigationItems") @required(action: THROW) {
					...ui_navigationKitTabListOld_TabList
					edges {
						__typename
					}
					errors {
						# eslint-disable-next-line @atlassian/relay/unused-fields
						message
						# eslint-disable-next-line @atlassian/relay/unused-fields
						extensions {
							statusCode
						}
					}
				}
			}
		`,
		jiraQueryFragment,
	);

	const hideAppsTab = useHideAppsTab();

	const dataSource = data.navigationItems;

	useEffect(() => {
		if (dataSource?.errors && dataSource?.errors?.length > 0) {
			const error = dataSource.errors[0];

			handleError(new QueryError(error));
		}
	}, [data, handleError, dataSource?.errors, dataSource?.edges]);

	const canEdit = projectDetails?.currentProject?.permissions.administerProject === true;

	// The apps tab filters itself on the frontend, but is returned in the list of tabs
	// As such, we need to remove it from the store so its hidden from drag and drop logic
	useEffect(() => {
		hideAppsTab();
	}, [hideAppsTab, dataSource]);

	const attributes = useMemo(
		() => ({
			projectType: (projectDetails?.currentProject?.type ?? '').toLowerCase(),
			navigationContainer: TAB,
		}),
		[projectDetails?.currentProject?.type],
	);

	return dataSource && !dataSource.errors && dataSource.edges ? (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName="businessHorizontalNav"
			containerType={PROJECT}
			containerId={String(projectDetails?.currentProject?.id ?? '')}
			attributes={attributes}
		>
			<ShortcutsContainer data={shortcutsData} loading={loading} isGlobal>
				<TabsConfigurationProvider tabsConfiguration={tabsConfiguration}>
					<TabList
						tabsFragment={dataSource}
						addTab={canEdit && <AddTabOld />}
						typeKeyToRoutesMapping={ITEM_TYPE_KEY_TO_ROUTES}
					/>
				</TabsConfigurationProvider>
				<ExperienceSuccess experience={VIEW_HORIZONTAL_NAVIGATION_EXPERIENCE} />
			</ShortcutsContainer>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	) : (
		<SkeletonComponent />
	);
};

export default memo(TabsContent);
